import React from 'react';
import { usePermissions } from '../../user/permissions';
import { useHistory } from 'react-router-dom';
import { useSegment } from '../tracking/trackingProvider';
import useAppContext from '../../context/useAppContext';

import { ContextTemplate } from '../template/template';
import { Trans, useTranslation } from 'react-i18next';
import { Icon, Button, Menu, NavMenuItem, NavMenuBody, NavMenu, MenuSeparator } from '@weezevent/nacre';

export function isNotPyvarOnly(permissions, organization, event) {
    // true only for non pyvar_only events or devs
    return !event?.pyvar_only || permissions.isDev(organization.pk);
}

export function MenuOrga({ organization, isLevelEvent }) {
    const { capacities } = React.useContext(ContextTemplate);
    const basename = `/ticket/O${organization.id}`;
    const [{ moderationConfig }] = useAppContext();
    const { t } = useTranslation();
    const { track } = useSegment();
    const permissions = usePermissions();
    const history = useHistory();

    const scopeAdvancedConfig = [
        'PyvarCapacity.ADMIN',
        'PyvarCapacity.CROSS_SELLING',
        'PyvarCapacity.RATE',
        'PyvarCapacity.TAGS',
        'PyvarCapacity.SERVICE',
        'PyvarCapacity.DESIGN_ADMIN',
        'PyvarCapacity.DESIGN_TEMPLATES',
        'PyvarCapacity.DESIGN'
    ];

    const hasCapacityCreateEvent = React.useMemo(() => {
        return permissions.hasCapacities(['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT_CREATE'], organization.id);
    }, [organization, permissions]);

    const displayAdvancedSettings = React.useMemo(() => {
        return permissions.hasCapacities([...scopeAdvancedConfig, 'PyvarCapacity.CHART'], organization.id);
    }, [organization, permissions]);

    const linkButtonEvent = hasCapacityCreateEvent && { href: `/ticket/O${organization.id}/events/new` };
    return (
        <Menu
            useNew
            capacities={capacities}
            alternative
            cta={
                <Button
                    primary
                    fluid
                    align="left"
                    onlyIconMobile={false}
                    label={t('context_menu.cta_label_orga')}
                    disabled={!hasCapacityCreateEvent}
                    icon={<Icon name="plus" />}
                    component="a"
                    onClick={() => {
                        if (hasCapacityCreateEvent) {
                            history.push(`/ticket/O${organization.id}/events/new`);
                            track('ACTION > CREATE AN EVENT MENU', {
                                'Page Title': 'EVENT LIST'
                            });
                        }
                    }}
                    {...linkButtonEvent}
                />
            }
        >
            <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}>
                <NavMenuItem active={Boolean(isLevelEvent)} icon="Calendar-Star-inverted" to={`${basename}/events`}>
                    <Trans i18nKey="main_menu_events">Events</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu allowedCapacities={['PyvarCapacity.ACCOMMODATION', 'PyvarCapacity.TRANSPORTS', 'PyvarCapacity.GIFT_CARD', 'PyvarCapacity.EXTRAS']}>
                <NavMenuItem icon="purchase-tag">
                    <Trans i18nKey="ticket.menu.orga.product-catalog.title">Products catalog</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={`${basename}/extras`} allowedCapacities={['PyvarCapacity.EXTRAS']}>
                        <Trans i18nKey="ticket.menu.orga.product-catalog.extras">Extras</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/gift_vouchers`} allowedCapacities={['PyvarCapacity.GIFT_CARD']}>
                        <Trans i18nKey="ticket.menu.orga.product-catalog.gift_vouchers">Gift vouchers</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/accommodations`} allowedCapacities={['PyvarCapacity.ACCOMMODATION']}>
                        <Trans i18nKey="ticket.menu.orga.product-catalog.accommodations">Accommodations</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/transports`} allowedCapacities={['PyvarCapacity.TRANSPORTS']}>
                        <Trans i18nKey="ticket.menu.orga.product-catalog.transports">Transports</Trans>
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>
            <NavMenu
                allowedCapacities={[
                    'PyvarCapacity.ADMIN',
                    'PyvarCapacity.WIDGET',
                    'PyvarCapacity.MODULES_ADMIN',
                    'PyvarCapacity.MODULES',
                    'PyvarCapacity.TICKET_OFFICE',
                    'PyvarCapacity.WEBSERVICE',
                    'PyvarCapacity.RESALE',
                    'PyvarCapacity.PARTNER',
                    'PyvarCapacity.PERSONALSPACE_ADMIN',
                    'PyvarCapacity.PERSONALSPACE'
                ]}
            >
                <NavMenuItem icon="cart-inverted">
                    <Trans i18nKey="ticket.menu.orga.channels">Sales channels</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={`${basename}/widgets`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.WIDGET']}>
                        <Trans i18nKey="ticket.menu.orga.modules">Modules</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/ticket_shop`} allowedCapacities={['PyvarCapacity.MODULES_ADMIN', 'PyvarCapacity.MODULES_SHOP']}>
                        <Trans i18nKey="ticket.menu.orga.online-ticketing">Online Ticketing</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/ticket_offices`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.TICKET_OFFICE']}>
                        <Trans i18nKey="ticket.menu.orga.ticket-offices">Ticket offices</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/webservices`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.WEBSERVICE']}>
                        <Trans i18nKey="ticket.menu.orga.webservices">Automatic Import</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/resale`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.RESALE']}>
                        <Trans i18nKey="ticket.menu.orga.resale">Resale</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/partners`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.PARTNER']}>
                        <Trans i18nKey="ticket.menu.orga.partners">Manage partners</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/personal_spaces`} allowedCapacities={['PyvarCapacity.PERSONALSPACE_ADMIN', 'PyvarCapacity.PERSONALSPACE']}>
                        <Trans i18nKey="ticket.menu.orga.personal-space">Personal spaces</Trans>
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>
            <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION', 'PyvarCapacity.TRANSFERS', 'PyvarCapacity.ACCOUNTING']}>
                <NavMenuItem icon="receipt-inverted">
                    <Trans i18nKey="ticket.menu.orga.invoicing">Invoicing</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={`${basename}/organization/accounting`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION', 'PyvarCapacity.ACCOUNTING']}>
                        <Trans i18nKey="ticket.menu.orga.accounting">Accounting</Trans>
                    </NavMenuItem>
                    {!organization.isFranchise() && (
                        <NavMenuItem to={`${basename}/organization/transfers`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.TRANSFERS']}>
                            <Trans i18nKey="ticket.menu.orga.transfers">Transfers history</Trans>
                        </NavMenuItem>
                    )}
                </NavMenuBody>
            </NavMenu>

            <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION', 'PyvarCapacity.BANK', 'PyvarCapacity.AFFILIATION']}>
                <NavMenuItem icon="institution">
                    <Trans i18nKey="ticket.menu.orga.orga">Organization</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={`${basename}/organization/profile`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION']}>
                        <Trans i18nKey="ticket.menu.orga.profile">Profile</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/organization/bank`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.BANK']}>
                        <Trans i18nKey="ticket.menu.orga.bank">Bank</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/organization/settings`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION']}>
                        <Trans i18nKey="ticket.menu.orga.settings">Settings</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/organization/affiliation`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.AFFILIATION']}>
                        <Trans i18nKey="ticket.menu.orga.affiliation">Affiliation</Trans>
                    </NavMenuItem>
                    {/* The distributor export only exists on the old back office, reachable from
                        this page, so it stays in the menu until TXT-13659 migrates it to pyvar. */}
                    <NavMenuItem to={`${basename}/statistics/reports`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION']}>
                        <Trans i18nKey="ticket.menu.orga.reports">Reports</Trans>
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>

            {/* Kept internal until the page is ready to be shown to organisers. */}
            <NavMenu allowedCapacities={['PyvarCapacity.DEV']}>
                <NavMenuItem to={`${basename}/exports`} icon="File-export">
                    <Trans i18nKey="ticket.menu.orga.exports">Exports</Trans>
                </NavMenuItem>
            </NavMenu>
            {displayAdvancedSettings && <MenuSeparator>{t('main_menu_advanced')}</MenuSeparator>}
            <NavMenu allowedCapacities={scopeAdvancedConfig}>
                <NavMenuItem icon="cog-inverted">
                    <Trans i18nKey="ticket.menu.orga.configuration">Configuration</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={`${basename}/products`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.CROSS_SELLING']}>
                        <Trans i18nKey="ticket.menu.orga.cross-selling">Cross-selling</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/rates`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.RATE_TEMPLATE']}>
                        <Trans i18nKey="ticket.menu.orga.rates.template">Rates template</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/tags`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.TAGS']}>
                        <Trans i18nKey="ticket.menu.orga.tags">Tags</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/services`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.SERVICE']}>
                        <Trans i18nKey="ticket.menu.orga.services">Services</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/api`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.API']}>
                        <Trans i18nKey="ticket.menu.orga.api">API</Trans>
                    </NavMenuItem>
                    {organization?.feature_flags.includes('EO_MEMBER_AREA') && (
                        <NavMenuItem to={`${basename}/member-space`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.MEMBER_SPACE']}>
                            <Trans i18nKey="ticket.menu.orga.member-space">Member space</Trans>
                        </NavMenuItem>
                    )}
                    <NavMenuItem
                        to={`${basename}/tickets_design/designs`}
                        allowedCapacities={['PyvarCapacity.DESIGN', 'PyvarCapacity.DESIGN_ADMIN', 'PyvarCapacity.DESIGN_TEMPLATES']}
                    >
                        <Trans i18nKey="ticket.menu.orga.tickets-design">Tickets designs</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/tickets_design/templates`} allowedCapacities={['PyvarCapacity.DESIGN_ADMIN', 'PyvarCapacity.DESIGN_TEMPLATES']}>
                        <Trans i18nKey="ticket.menu.orga.tickets-template">Tickets templates</Trans>
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>

            <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.CHART']}>
                <NavMenuItem to={`${basename}/charts`} icon="map-pin-inverted">
                    <Trans i18nKey="ticket.menu.orga.charts">Numeroted seating</Trans>
                </NavMenuItem>
            </NavMenu>

            <NavMenu allowedCapacities={['PyvarCapacity.SUBSCRIPTION_PLAN']}>
                <NavMenuItem icon="users">
                    <Trans i18nKey="ticket.menu.communities.communities">Communities</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={`${basename}/memberships`}>
                        <Trans i18nKey="ticket.menu.communities.memberships">Memberships</Trans>
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>

            <NavMenu allowedCapacities={['PyvarCapacity.MODERATION']}>
                <NavMenuItem icon="check-shield">
                    <Trans i18nKey="ticket.menu.orga.moderation">Moderation</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem exact to={`${basename}/moderation`}>
                        <Trans i18nKey="ticket.menu.orga.dashboard">Dashboard</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/moderation/moderation_${moderationConfig?.context ?? 'attendees'}`}>
                        <Trans i18nKey={`ticket.menu.orga.${moderationConfig?.context ?? 'attendees'}`}>Attendees</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/moderation/moderation_users`} allowedCapacities={['PyvarCapacity.MODERATION_ADMIN']}>
                        <Trans i18nKey="ticket.menu.orga.operators">Operators</Trans>
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>
        </Menu>
    );
}

export function SettingsNavMenu({ basename, event }) {
    return (
        <NavMenu
            allowedCapacities={[
                'PyvarCapacity.ADMIN',
                'PyvarCapacity.EVENT',
                'PyvarCapacity.SESSION',
                'PyvarCapacity.RATE',
                'PyvarCapacity.FORM',
                'PyvarCapacity.DESIGN_ADMIN',
                'PyvarCapacity.DESIGN_TEMPLATES',
                'PyvarCapacity.DESIGN'
            ]}
        >
            <NavMenuItem icon="cog">
                <Trans i18nKey="ticket.common.action.configure">Configure</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/settings/event`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}>
                    <Trans i18nKey="ticket.menu.event.details">Event details</Trans>
                </NavMenuItem>
                {event.use_sessions && (
                    <NavMenuItem to={`${basename}/settings/sessions`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.SESSION']}>
                        <Trans i18nKey="ticket.menu.event.sessions">Sessions</Trans>
                    </NavMenuItem>
                )}
                <NavMenuItem to={`${basename}/settings/rates`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.RATE']}>
                    <Trans i18nKey="ticket.menu.event.rates">Ticket types and selling</Trans>
                </NavMenuItem>
                <NavMenuItem
                    to={`${basename}/settings/tickets_design/designs`}
                    allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.DESIGN_ADMIN', 'PyvarCapacity.DESIGN_TEMPLATES', 'PyvarCapacity.DESIGN']}
                >
                    <Trans i18nKey="ticket.menu.event.tickets-design">Ticket design</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/settings/forms`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.FORM']}>
                    <Trans i18nKey="ticket.menu.event.forms">Forms</Trans>
                </NavMenuItem>
                {event?.feature_flags.includes('EO_ADHEREVENT') && (
                    <NavMenuItem to={`${basename}/settings/subscriptions`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.SUBSCRIPTIONS']}>
                        <Trans i18nKey="ticket.menu.event.subscriptions">Subscriptions</Trans>
                    </NavMenuItem>
                )}
            </NavMenuBody>
        </NavMenu>
    );
}

export function ProductTypeNavMenu({ basename }) {
    return (
        <NavMenu allowedCapacities={['PyvarCapacity.ACCOMMODATION', 'PyvarCapacity.TRANSPORTS', 'PyvarCapacity.EXTRAS']}>
            <NavMenuItem icon="tickets">
                <Trans i18nKey="ticket.personal_spaces.step.product_types.menu-title">Tickets</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/settings/tickets/rates`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.RATE']}>
                    <Trans i18nKey="ticket.personal_spaces.step.product_types.tickets">Tickets</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/settings/accommodations/rates`} allowedCapacities={['PyvarCapacity.ACCOMMODATION']}>
                    <Trans i18nKey="ticket.personal_spaces.step.product_types.accommodations">Accommodations</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/settings/transports/rates`} allowedCapacities={['PyvarCapacity.TRANSPORTS']}>
                    <Trans i18nKey="ticket.personal_spaces.step.product_types.transports">Transports</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/settings/extras/rates`} allowedCapacities={['PyvarCapacity.EXTRAS']}>
                    <Trans i18nKey="ticket.personal_spaces.step.product_types.extras">Extras</Trans>
                </NavMenuItem>
            </NavMenuBody>
        </NavMenu>
    );
}

export function SeatingNavMenu({ basename, organization, event }) {
    return (
        <NavMenu
            allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_AVAILABILITY', 'PyvarCapacity.SEATING_QUOTA', 'PyvarCapacity.CONTINGENT', 'PyvarCapacity.CHART']}
        >
            <NavMenuItem icon="map">
                <Trans i18nKey="ticket.menu.event.seating">Seating settings</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/seating/availability`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_AVAILABILITY']}>
                    <Trans i18nKey="ticket.menu.chart.availability">Availability</Trans>
                </NavMenuItem>
                {organization?.quota_pyvar_enabled || event?.pyvar_only ? (
                    <NavMenuItem to={`${basename}/seating/quotas_dev`} allowedCapacities={['PyvarCapacity.QUOTAS']}>
                        <Trans i18nKey="ticket.menu.chart.quota">Quotas</Trans>
                    </NavMenuItem>
                ) : (
                    <NavMenuItem to={`${basename}/seating/quotas`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_QUOTA']}>
                        <Trans i18nKey="ticket.menu.chart.quota">Quotas</Trans>
                    </NavMenuItem>
                )}
                <NavMenuItem to={`/ticket/O${organization.id}/charts/${event.chart}/contingents`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.CONTINGENT']}>
                    <Trans i18nKey="ticket.menu.chart.contingents">Contingents</Trans>
                </NavMenuItem>
                <NavMenuItem to={`/ticket/O${organization.id}/charts/${event.chart}/dashboard`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.CHART']}>
                    <Trans i18nKey="ticket.menu.event.chart">Numeroted seating</Trans>
                </NavMenuItem>
            </NavMenuBody>
        </NavMenu>
    );
}

export function CommunicationNavMenu({ basename, permissions, organization, event }) {
    const showNotPyvarOnly = React.useMemo(() => isNotPyvarOnly(permissions, organization, event), [permissions, organization, event]);

    return (
        <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.DISCOUNT', 'PyvarCapacity.INVITATION']}>
            <NavMenuItem icon="message">
                <Trans i18nKey="ticket.menu.event.communicate">Communicate</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/communicate/discounts`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.DISCOUNT']}>
                    <Trans i18nKey="ticket.menu.event.discounts">Discount codes</Trans>
                </NavMenuItem>
                {/* <NavMenuItem to={`${basename}/communicate/indexing`}>
                    <Trans i18nKey="ticket.menu.event.seo">Referencing and exposure</Trans>
                </NavMenuItem> */}
                {!event.isRealSeating() && showNotPyvarOnly && (
                    <NavMenuItem to={`${basename}/communicate/invitations`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.INVITATION']}>
                        <Trans i18nKey="ticket.menu.event.invitations">Invitations Light</Trans>
                    </NavMenuItem>
                )}
                <NavMenuItem to={`${basename}/communicate/emails`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.EMAILS_EXPORT']}>
                    <Trans i18nKey="ticket.menu.event.email-export">Export e-mails</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/communicate/qr-code`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.WIDGET', 'PyvarCapacity.WIDGET_ADMIN']}>
                    <Trans i18nKey="ticket.menu.event.qr-code">QR code</Trans>
                </NavMenuItem>
            </NavMenuBody>
        </NavMenu>
    );
}

export function SellNavMenu({ basename, permissions, organization, event }) {
    const showNotPyvarOnly = React.useMemo(() => isNotPyvarOnly(permissions, organization, event), [permissions, organization, event]);

    return (
        <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.WIDGET', 'PyvarCapacity.MINISITE', 'PyvarCapacity.PRINTER', 'PyvarCapacity.TICKET_OFFICE']}>
            <NavMenuItem icon="cart">
                <Trans i18nKey="ticket.menu.event.sell">Sell</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/channels/widget`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.WIDGET']}>
                    <Trans i18nKey="ticket.menu.event.module">On your own website</Trans>
                </NavMenuItem>
                {/* <NavMenuItem to={`${basename}/channels/facebook`}>
                    <Trans i18nKey="ticket.menu.event.facebook">On Facebook</Trans>
                </NavMenuItem> */}
                <NavMenuItem to={`${basename}/channels/page`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.MINISITE']}>
                    <Trans i18nKey="ticket.menu.event.minisite">On your minisite</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/ticket_offices`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.TICKET_OFFICE']}>
                    <Trans i18nKey="ticket.menu.event.ticket-office">Available at ticket offices</Trans>
                </NavMenuItem>
                {showNotPyvarOnly && (
                    <NavMenuItem to={`${basename}/attendees/stock-print`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.PRINTER']}>
                        <Trans i18nKey="ticket.menu.event.stock-print">Print stock</Trans>
                    </NavMenuItem>
                )}
            </NavMenuBody>
        </NavMenu>
    );
}

export function ResaleNavMenu({ basename }) {
    return (
        <NavMenu allowedCapacities={['PyvarCapacity.INTERNAL_RESALE', 'PyvarCapacity.INTERNAL_RESALE_ADMIN']}>
            <NavMenuItem icon="transfer">
                <Trans i18nKey="ticket.menu.event.resale">Resale</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/resale/activation`} allowedCapacities={['PyvarCapacity.INTERNAL_RESALE', 'PyvarCapacity.INTERNAL_RESALE_ADMIN']}>
                    <Trans i18nKey="ticket.menu.event.resale-activation">Activation</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/resale/settings`} allowedCapacities={['PyvarCapacity.INTERNAL_RESALE', 'PyvarCapacity.INTERNAL_RESALE_ADMIN']}>
                    <Trans i18nKey="ticket.menu.event.resale-settings">Resale settings</Trans>
                </NavMenuItem>
            </NavMenuBody>
        </NavMenu>
    );
}

export function AttendeesNavMenu({ basename, permissions, organization, event }) {
    const showNotPyvarOnly = React.useMemo(() => isNotPyvarOnly(permissions, organization, event), [permissions, organization, event]);

    return (
        <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.ATTENDEE']}>
            <NavMenuItem icon="users">
                <Trans i18nKey="ticket.menu.event.attendees.attendees">Attendees</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/manager/attendees`}>
                    <Trans i18nKey="ticket.menu.event.attendees.manage">Manage attendees</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/manager/orders`}>
                    <Trans i18nKey="ticket.menu.event.orders">Manage orders</Trans>
                </NavMenuItem>
                {event?.feature_flags.includes('EO_ADHEREVENT') && (
                    <NavMenuItem to={`${basename}/attendees/members`}>
                        <Trans i18nKey="ticket.menu.event.members">Manage members</Trans>
                    </NavMenuItem>
                )}
                {showNotPyvarOnly && (
                    <NavMenuItem to={`${basename}/attendees/imports`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.IMPORT_ATTENDEES']}>
                        <Trans i18nKey="ticket.menu.event.attendees.import">Import attendees</Trans>
                    </NavMenuItem>
                )}
                <NavMenuItem to={`${basename}/attendees/add`}>
                    <Trans i18nKey="ticket.menu.event.attendees.add">Add attendees</Trans>
                </NavMenuItem>
            </NavMenuBody>
        </NavMenu>
    );
}

export function StatisticsNavMenu({ basename, event }) {
    return (
        <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}>
            <NavMenuItem icon="chart">
                <Trans i18nKey="ticket.menu.event.statistics.statistics">Statistics</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/statistics/overview`} allowedCapacities={['PyvarCapacity.DEV']}>
                    <Trans i18nKey="ticket.menu.event.statistics.overview">Overview</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/statistics/sales`} allowedCapacities={['PyvarCapacity.DEV']}>
                    <Trans i18nKey="ticket.menu.event.statistics.sales">Sales</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/statistics/traffic`} allowedCapacities={['PyvarCapacity.DEV']}>
                    <Trans i18nKey="ticket.menu.event.statistics.traffic">Traffic</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/statistics/advanced-data`} allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}>
                    <Trans i18nKey="ticket.menu.event.statistics.advanced-data">Advanced Data</Trans>
                </NavMenuItem>
            </NavMenuBody>
        </NavMenu>
    );
}

export function TicketingOptionsNavMenu({ basename, event }) {
    return (
        <NavMenu
            allowedCapacities={['PyvarCapacity.SALE_OPTIONS', 'PyvarCapacity.ANALYTICS', 'PyvarCapacity.PURCHASESCENARIOS', 'PyvarCapacity.QUEUE', 'PyvarCapacity.QUEUE_EDITOR']}
        >
            <NavMenuItem icon="controle">
                <Trans i18nKey="ticket.menu.event.options.options">Ticketing options</Trans>
            </NavMenuItem>
            <NavMenuBody>
                <NavMenuItem to={`${basename}/sale/moduledisplay`} allowedCapacities={['PyvarCapacity.SALE_OPTIONS']}>
                    <Trans i18nKey="ticket.menu.event.options.display">Module display</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/sale/payementinvoicing`} allowedCapacities={['PyvarCapacity.SALE_OPTIONS']}>
                    <Trans i18nKey="ticket.menu.event.options.payment">Payement and Invoicing</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/sale/notification`} allowedCapacities={['PyvarCapacity.SALE_OPTIONS']}>
                    <Trans i18nKey="ticket.menu.event.options.notifications">Notification</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/sale/additionalservice`} allowedCapacities={['PyvarCapacity.SALE_OPTIONS']}>
                    <Trans i18nKey="ticket.menu.event.options.services">Additional services</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/sale/integration`} allowedCapacities={['PyvarCapacity.ANALYTICS']}>
                    <Trans i18nKey="ticket.menu.event.options.integrations">Integrations</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/sale/scenarios`} allowedCapacities={['PyvarCapacity.PURCHASESCENARIOS']}>
                    <Trans i18nKey="ticket.menu.event.options.purchase_scenarios">Purchase scenarios</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/settings/queue/E${event.id}`} allowedCapacities={['PyvarCapacity.QUEUE', 'PyvarCapacity.QUEUE_EDITOR']}>
                    <Trans i18nKey="ticket.menu.event.options.virtual_queue">Virtual queue</Trans>
                </NavMenuItem>
                {event.pyvar_only && (
                    <NavMenuItem to={`${basename}/advanced-prices/configs`} allowedCapacities={['PyvarCapacity.PRICING']}>
                        <Trans i18nKey="ticket.menu.event.options.advanced_pricing">Advanced pricing</Trans>
                    </NavMenuItem>
                )}
            </NavMenuBody>
        </NavMenu>
    );
}

export function SideMenuEvent({ organization, event }) {
    if (!organization || !event) {
        return [];
    }
    const { capacities } = React.useContext(ContextTemplate);
    const permissions = usePermissions();
    const basename = `/ticket/O${organization.id}/E${event.id}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}>
                <NavMenuItem icon="home" to={`${basename}/dashboard`} exact>
                    <Trans i18nKey="ticket.menu.event.dashboard">Dashboard</Trans>
                </NavMenuItem>
            </NavMenu>
            <SettingsNavMenu basename={basename} event={event} />
            <ProductTypeNavMenu basename={basename} organization={organization} />
            {event.isRealSeating() && <SeatingNavMenu basename={basename} event={event} organization={organization} />}
            <CommunicationNavMenu basename={basename} permissions={permissions} organization={organization} event={event} />
            <SellNavMenu basename={basename} permissions={permissions} organization={organization} event={event} />
            {event.hasChart() && <ResaleNavMenu basename={basename} />}
            <AttendeesNavMenu basename={basename} permissions={permissions} organization={organization} event={event} />
            <StatisticsNavMenu basename={basename} event={event} />
            <TicketingOptionsNavMenu basename={basename} event={event} />
        </Menu>
    );
}

export function SideMenuPartner({ organizationId, partnerId }) {
    const { capacities } = React.useContext(ContextTemplate);
    const basename = `/ticket/O${organizationId}/partners/${partnerId}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                <NavMenuItem to={`${basename}/dashboard`} icon="home">
                    <Trans i18nKey="ticket.menu.partner.dashboard">Dashboard</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem to={`${basename}/batches`} icon="basket">
                    <Trans i18nKey="ticket.menu.partner.batches">Booking Batches</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem to={`${basename}/edit`} icon="id-card">
                    <Trans i18nKey="ticket.menu.partner.settings">General informations</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem to={`${basename}/users`} icon="users">
                    <Trans i18nKey="ticket.menu.partner.users">Users</Trans>
                </NavMenuItem>
            </NavMenu>
        </Menu>
    );
}

export function SideMenuPersonalSpace({ organizationId, personal_space }) {
    const { capacities } = React.useContext(ContextTemplate);
    const basename = `/ticket/O${organizationId}/personal_spaces/${personal_space.id}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                <NavMenuItem to={`${basename}/settings`} icon="cog">
                    <Trans i18nKey="ticket.menu.personal-space.settings">Settings</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem to={`${basename}/translations`} icon="edit">
                    <Trans i18nKey="ticket.menu.personal-space.translations">Translations</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem to={`${basename}/origins`} icon="check-shield" allowedCapacities={['PyvarCapacity.WIDGET_ORIGIN']}>
                    <Trans i18nKey="ticket.menu.online-ticketing.origins">Origins</Trans>
                </NavMenuItem>
            </NavMenu>
        </Menu>
    );
}

export function SideMenuProduct({ organizationId, product }) {
    const { capacities } = React.useContext(ContextTemplate);
    const basename = `/ticket/O${organizationId}/products/${product.id}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                <NavMenuItem to={`${basename}/settings`} icon="cog">
                    <Trans i18nKey="ticket.menu.product.settings">Settings</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem to={`${basename}/variants`} icon="list-ul">
                    <Trans i18nKey="ticket.menu.product.variants">Variants</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem to={`${basename}/activation`} icon="calendar-star">
                    <Trans i18nKey="ticket.menu.product.activate_online">Online activation</Trans>
                </NavMenuItem>
            </NavMenu>
        </Menu>
    );
}

export function SideMenuOnlineTicketing({ organizationId, ticket_shop }) {
    const { capacities } = React.useContext(ContextTemplate);
    const permissions = usePermissions();

    const basename = `/ticket/O${organizationId}/ticket_shop/${ticket_shop.id}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                <NavMenuItem to={`${basename}/settings`} icon="cog">
                    <Trans i18nKey="ticket.menu.personal-space.settings">Settings</Trans>
                </NavMenuItem>
            </NavMenu>
            {ticket_shop.usage === 'sale' && permissions.hasCapacity('PyvarCapacity.MODULES_ADMIN', organizationId) && (
                <NavMenu>
                    <NavMenuItem to={`${basename}/steps`} icon="controle">
                        <Trans i18nKey="ticket.menu.personal-space.steps">Steps</Trans>
                    </NavMenuItem>
                </NavMenu>
            )}
            <NavMenu>
                <NavMenuItem to={`${basename}/translations`} icon="edit">
                    <Trans i18nKey="ticket.menu.personal-space.translations">Translations</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem
                    to={`${basename}/links`}
                    icon="link"
                    allowedCapacities={['PyvarCapacity.MODULES_ADMIN', 'PyvarCapacity.MODULES_SHOP_ADMIN', 'PyvarCapacity.MODULES_SHOP']}
                >
                    <Trans i18nKey="ticket.menu.online-ticketing.deeplinks">Deep Links</Trans>
                </NavMenuItem>
            </NavMenu>
            <NavMenu>
                <NavMenuItem to={`${basename}/origins`} icon="check-shield" allowedCapacities={['PyvarCapacity.WIDGET_ORIGIN']}>
                    <Trans i18nKey="ticket.menu.online-ticketing.origins">Origins</Trans>
                </NavMenuItem>
            </NavMenu>
        </Menu>
    );
}

export function SideMenuTicketOffices({ organizationId, ticket_office }) {
    const { capacities } = React.useContext(ContextTemplate);
    const basename = `/ticket/O${organizationId}/ticket_offices/${ticket_office.id}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                <NavMenuItem to={`${basename}/dashboard`} icon="home" exact>
                    <Trans i18nKey="ticket.menu.ticket-office.dashboard">Dashboard</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/users`} icon="user">
                    <Trans i18nKey="ticket.menu.ticket-office.operators">Operators</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/events`} icon="calendar-star">
                    <Trans i18nKey="ticket.menu.ticket-office.events">Events</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/settings`} icon="cog">
                    <Trans i18nKey="ticket.menu.ticket-office.settings">Settings</Trans>
                </NavMenuItem>
                <NavMenuItem icon="chart">
                    <Trans i18nKey="ticket.menu.ticket-office.reports">Reports</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={`${basename}/reports/salesjournal`}>
                        <Trans i18nKey="ticket.menu.ticket-office.sales">Sales journal</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/reports/checkout`}>
                        <Trans i18nKey="ticket.menu.ticket-office.checkouts">Cash outgoings</Trans>
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>
        </Menu>
    );
}

export function SideMenuWebservices({ organizationId, webservice }) {
    const { capacities } = React.useContext(ContextTemplate);
    const basename = `/ticket/O${organizationId}/webservices/${webservice.id}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                {/* TODO: V2 */}
                {/* <NavMenuItem to={`${basename}/dashboard`} icon="home" exact>
                    <Trans i18nKey="ticket.menu.chart.dashboard">Dashboard</Trans>
                </NavMenuItem> */}
                <NavMenuItem to={`${basename}/attendees?validity__in=valid`} icon="group">
                    <Trans i18nKey="ticket.menu.webservices.attendees">Attendees</Trans>
                </NavMenuItem>
                {webservice.default_provider !== 'seetickets' && webservice.type === 'hook' && (
                    <NavMenuItem to={`${basename}/mapping`} icon="controle">
                        <Trans i18nKey="ticket.menu.webservices.mapping">Mapping</Trans>
                    </NavMenuItem>
                )}
                <NavMenuItem to={`${basename}/subscription_links`} icon="download">
                    <Trans i18nKey="ticket.menu.webservices.management-rules">WeezTicket import</Trans>
                </NavMenuItem>
            </NavMenu>
        </Menu>
    );
}

export function SideMenuCharts({ organizationId, chart }) {
    const { capacities } = React.useContext(ContextTemplate);
    const basename = `/ticket/O${organizationId}/charts/${chart.pk}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                <NavMenuItem to={`${basename}/dashboard`} icon="home" exact>
                    <Trans i18nKey="ticket.menu.chart.dashboard">Dashboard</Trans>
                </NavMenuItem>
                <NavMenuItem icon="cog">
                    <Trans i18nKey="ticket.menu.chart.configuration">Configuration</Trans>
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={`${basename}/categories`}>
                        <Trans i18nKey="ticket.menu.chart.categories">Categories</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/zones`}>
                        <Trans i18nKey="ticket.menu.chart.zones">Zones</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/inventories`}>
                        <Trans i18nKey="ticket.menu.chart.inventories">Inventories</Trans>
                    </NavMenuItem>
                    <NavMenuItem to={`${basename}/contingents`}>
                        <Trans i18nKey="ticket.menu.chart.contingents">Contingents</Trans>
                    </NavMenuItem>
                </NavMenuBody>
                <NavMenuItem to={`${basename}/customs`} icon="color-fill">
                    <Trans i18nKey="ticket.menu.chart.customization">Personnalization</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/global_settings`} icon="controle">
                    <Trans i18nKey="ticket.menu.chart.settings">Settings</Trans>
                </NavMenuItem>
            </NavMenu>
        </Menu>
    );
}

export function SideMenuCommunities({ organizationId, membership }) {
    const { capacities } = React.useContext(ContextTemplate);
    const basename = `/ticket/O${organizationId}/memberships/${membership.id}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                <NavMenuItem to={`${basename}`} icon="users" exact>
                    <Trans i18nKey="ticket.membership.members.menu_title">Members</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/perks`} icon="offer" allowedCapacities={['PyvarCapacity.SUBSCRIPTION_PERK']}>
                    <Trans i18nKey="ticket.membership.menu.perks">Advantages and perks</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/settings`} icon="cog">
                    <Trans i18nKey="ticket.menu.settings">Settings</Trans>
                </NavMenuItem>
                <NavMenuItem to={`${basename}/email-settings`} icon="envelope">
                    <Trans i18nKey="ticket.membership.menu.email_settings">Notifications e-mail</Trans>
                </NavMenuItem>
            </NavMenu>
        </Menu>
    );
}

export function SideMenuModeration({ organizationId, flowId }) {
    const { t } = useTranslation();
    const { capacities } = React.useContext(ContextTemplate);
    const [{ moderationConfig }] = useAppContext();

    const basename = `/ticket/O${organizationId}`;

    return (
        <Menu useNew capacities={capacities}>
            <NavMenu>
                <NavMenuItem
                    to={`${basename}/moderation/moderation_flows/${flowId}/moderation_${moderationConfig?.context ?? 'attendees'}`}
                    icon={moderationConfig?.icon ?? 'users'}
                >
                    {t(`ticket.moderation.contextMenu.${moderationConfig?.context ?? 'attendees'}`)}
                </NavMenuItem>
                <NavMenuItem to={`${basename}/moderation/moderation_flows/${flowId}/moderation_users`} icon="user" allowedCapacities={['PyvarCapacity.ADMIN']}>
                    {t('ticket.moderation.contextMenu.operators')}
                </NavMenuItem>
                <NavMenuItem to={`${basename}/moderation/moderation_flows/${flowId}/files`} icon="File-export" allowedCapacities={['PyvarCapacity.MODERATION_EXPORTS']}>
                    {t('ticket.moderation.contextMenu.exports')}
                </NavMenuItem>
                <NavMenuItem to={`${basename}/moderation/moderation_flows/${flowId}/statuses_transitions`} icon="check-circle" allowedCapacities={['PyvarCapacity.ADMIN']}>
                    {t('ticket.moderation.contextMenu.status')}
                </NavMenuItem>
                <NavMenuItem
                    to={`${basename}/moderation/moderation_flows/${flowId}/settings`}
                    icon="cog"
                    allowedCapacities={['PyvarCapacity.ADMIN', 'PyvarCapacity.MODERATION_ADMIN', 'PyvarCapacity.MODERATION_SETTINGS']}
                >
                    {t('ticket.moderation.contextMenu.settings')}
                </NavMenuItem>
            </NavMenu>
        </Menu>
    );
}
